import { Controller } from "@hotwired/stimulus"
import Postal from "postal"

// Exposed on window so feature specs can simulate a completed Filestack
// upload (window.Postal.channel("file_picker").publish(...)) without
// driving the real third-party upload widget.
window.Postal = Postal

const FILESTACK_API_KEY = "A75mbkn0eRzSzKS3aaeCrz"
const MAX_FILE_SIZE_BYTES = 10485760

// Uploads go straight through the Filestack SDK (loaded globally via a
// script tag in the page head) instead of the removed React
// AdvancedFilePicker component.
export default class extends Controller {
  static get targets() {
    return ["body"]
  }

  static get values() {
    return {
      blockId: Number,
      apiReference: String
    }
  }

  connect() {
    this.subscription = Postal.channel("file_picker").subscribe(
      `file_picker_${this.blockIdValue}_saving_success`,
      (fileAsset) => this.handleUploadSuccess(fileAsset)
    )

    if (this.apiReferenceValue) {
      this.fetchFileAsset()
    } else {
      this.renderPicker()
    }
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
  }

  async fetchFileAsset() {
    try {
      const response = await fetch(`/api/v1/file-assets/${this.apiReferenceValue}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) throw new Error("Failed to load file asset")

      const data = await response.json()
      this.photo = data.data
      this.renderPhoto()
    } catch (error) {
      this.renderPicker()
    }
  }

  handleUploadSuccess(fileAsset) {
    this.photo = fileAsset.data
    this.apiReferenceValue = fileAsset.data.attributes.api_reference
    this.renderPhoto()
    this.dispatch("changed", {
      detail: { blockData: { photo: fileAsset } },
      prefix: "homer-block"
    })
  }

  removePhoto() {
    this.photo = null
    this.apiReferenceValue = ""
    this.renderPicker()
    this.dispatch("changed", { detail: { blockData: {} }, prefix: "homer-block" })
  }

  renderPhoto() {
    const imgUrl = this.photo.attributes.is_processing
      ? `https://cdn.filestackcontent.com/resize=width:700/${this.photo.attributes.filestack_meta_info.handle}`
      : this.photo.attributes.url

    this.bodyTarget.innerHTML = `
      <img src="${this.escapeHtml(imgUrl)}">
      <div class="btn btn-default btn-milli" data-action="click->homer-photo-v1-block#removePhoto">Remove Photo</div>
    `
  }

  renderPicker() {
    this.bodyTarget.innerHTML = `
      <div class="photo-block-v1-editor__choose-prompt">Choose which photo you'd like to add.</div>
      <div class="photo-block-v1-editor__choose_btn">
        <div class="btn btn-default btn-primary btn-delta" data-action="click->homer-photo-v1-block#openPicker">Choose Photo</div>
      </div>
      <div class="hint">
        You can choose a photo from your device, Facebook or Instagram.<br>
        Note: you can only upload one photo at a time.
      </div>
    `
  }

  openPicker() {
    window.filestack.init(FILESTACK_API_KEY).pick({
      accept: "image/*",
      uploadInBackground: false,
      disableTransformer: true,
      hideWhenUploading: false,
      onFileSelected: (file) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          throw new Error(`Please select a file smaller than ${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB`)
        }
      },
      onFileUploadFinished: (file) => this.createFileAsset(file)
    })
  }

  async createFileAsset(file) {
    const response = await fetch("/api/v1/file-assets/", {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({
        data: {
          type: "HomerItem::Photo",
          attributes: {
            pre_processing_file_size: file.size,
            temp_uploaded_file_url: file.url,
            filestack_meta_info: file
          }
        }
      })
    })

    if (!response.ok) return

    const data = await response.json()
    Postal.channel("file_picker").publish(`file_picker_${this.blockIdValue}_saving_success`, data)
  }

  get csrfToken() {
    const csrfMetaTag = document.querySelector("meta[name='csrf-token']")
    return csrfMetaTag ? csrfMetaTag.content : ""
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
